<template>
  <div>
    <div>
      <BotEditorSubHeader
        :title="$t('botEditor.tools.title')"
        :subtitle="$t('botEditor.tools.subtitle')"
      />
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-8">
      <div class="text-gray-400">{{ $t('botEditor.tools.loadingText') }}</div>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="bg-red-900/20 border border-red-500 text-red-300 px-4 py-3 rounded-sm">
      {{ error }}
    </div>

    <!-- Grid dei tool configurati + card "+" -->
    <div v-else class="tools-grid">

      <!-- Card per ogni tool MCP HTTP configurato -->
      <div
        v-for="bms in configuredMcps"
        :key="bms._id"
        data-telemetry-id="tool-card-mcp"
        class="tool-card"
        :class="{ 'is-enabled': bms.enabled }"
      >
        <div class="card-header">
          <div class="tool-icon-container rounded-lg" :style="{ background: bmsIconColor(bms) }">
            <span class="material-icons">{{ bmsIcon(bms) }}</span>
          </div>
          <div class="tool-info">
            <h3>{{ bmsDisplayName(bms) }}</h3>
            <div class="flex gap-1.5 items-center">
              <Chip
                :label="bms.enabled ? $t('botEditor.tools.statusVisible') : $t('botEditor.tools.statusHidden')"
                :class="bms.enabled ? '!bg-green-500 !text-white' : '!bg-blue-500 !text-blue-100'"
                class="!text-xs !font-medium"
              />
              <Chip v-if="bms.serverType === 'custom_http'" label="HTTP" class="!bg-purple-600 !text-purple-100 !text-xs !font-medium" />
            </div>
          </div>
          <InfoTip :text="$t('botEditor.tools.visibilityTooltip')" position="left" />
          <!-- ToggleSwitch keeps its own checked state and only re-syncs when :model-value
               changes. A failed toggle leaves the model as is, so the catch bumps the
               nonce in the key: the switch remounts on the real value. -->
          <ToggleSwitch
            :key="`${bms._id}-${switchNonce[bms._id] || 0}`"
            :model-value="bms.enabled"
            :aria-label="bmsDisplayName(bms)"
            data-telemetry-id="btn-toggle-mcp"
            class="shrink-0"
            @update:model-value="(enabled) => toggleMcp(bms._id, enabled)"
          />
        </div>

        <div class="card-body">
          <p class="tool-description">{{ bmsDescription(bms) }}</p>
          <div class="tool-meta">
            <span class="text-xs bg-blue-900/50 text-blue-300 px-2 py-1 rounded-sm">custom</span>
          </div>

          <div class="mt-3 mb-3">
            <label class="block text-gray-400 text-sm mb-1">{{ $t('botEditor.tools.tagsLabel') }}</label>
            <TagMultiSelect
              :workspace-id="workspaceId"
              :bot-id="botId"
              :model-value="bms.tags || []"
              :placeholder="$t('botEditor.tools.tagsPlaceholder')"
              @update:model-value="updateMcpTags(bms, $event)"
            />
            <p class="text-gray-500 text-xs mt-1">{{ $t('botEditor.tools.tagsHelp') }}</p>
          </div>

          <div style="display:flex;gap:8px">
            <Button
              data-telemetry-id="btn-delete-mcp"
              severity="danger"
              class="w-full"
              @click="openDeleteModal(bms._id)"
            >
              <span class="material-icons">delete</span>
              {{ $t('botEditor.tools.deleteModalConfirm') }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Card per ogni custom tool flow -->
      <div
        v-for="tf in toolFlows"
        :key="tf._id"
        data-telemetry-id="tool-card-flow"
        class="tool-card"
        :class="{ 'is-enabled': tf.enabled }"
      >
        <div class="card-header">
          <div class="tool-icon-container rounded-lg" style="background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)">
            <span class="material-icons">build</span>
          </div>
          <div class="tool-info">
            <h3>{{ tf.name || $t('botEditor.tools.customToolDefaultName') }}</h3>
            <Chip
              :label="tf.enabled ? $t('botEditor.tools.statusVisible') : $t('botEditor.tools.statusHidden')"
              :class="tf.enabled ? '!bg-green-500 !text-white' : '!bg-blue-500 !text-blue-100'"
              class="!text-xs !font-medium"
            />
          </div>
          <InfoTip :text="$t('botEditor.tools.visibilityTooltip')" position="left" />
          <ToggleSwitch
            :key="`${tf._id}-${switchNonce[tf._id] || 0}`"
            :model-value="tf.enabled"
            :aria-label="tf.name || $t('botEditor.tools.customToolDefaultName')"
            data-telemetry-id="btn-toggle-tool-flow"
            class="shrink-0"
            @update:model-value="(enabled) => toggleToolFlow(tf._id, enabled)"
          />
        </div>

        <div class="card-body">
          <p class="tool-description">{{ $t('botEditor.tools.customToolDescription') }}</p>
          <div class="tool-meta">
            <span class="text-xs px-2 py-1 rounded-sm" style="background:#4c1d95;color:#ddd6fe">custom tool</span>
          </div>
          <div class="mt-3 mb-3">
            <label class="block text-gray-400 text-sm mb-1">{{ $t('botEditor.tools.tagsLabel') }}</label>
            <TagMultiSelect
              :workspace-id="workspaceId"
              :bot-id="botId"
              :model-value="tf.tags || []"
              :placeholder="$t('botEditor.tools.tagsPlaceholder')"
              @update:model-value="updateToolFlowTags(tf, $event)"
            />
            <p class="text-gray-500 text-xs mt-1">{{ $t('botEditor.tools.tagsHelp') }}</p>
          </div>

          <div style="display:flex;gap:8px">
            <Button data-telemetry-id="btn-edit-tool-flow" severity="primary" class="w-full" @click="editToolFlow(tf)">
              <span class="material-icons">edit</span>
              {{ $t('botEditor.tools.editButton') }}
            </Button>
            <Button data-telemetry-id="btn-delete-tool-flow" severity="danger" class="w-full" @click="openDeleteToolFlowModal(tf._id)">
              <span class="material-icons">delete</span>
              {{ $t('botEditor.tools.deleteModalConfirm') }}
            </Button>
          </div>
        </div>
      </div>

      <!-- Card "+" per aggiungere nuovi tool -->
      <div id="btn-request-add-tool" class="tool-card add-card" @click="showAddModal = true">
        <div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:12px;color:#6b7280">
          <span class="material-icons" style="font-size:48px">add_circle_outline</span>
          <span style="font-size:14px;font-weight:500">{{ $t('botEditor.tools.addToolLabel') }}</span>
        </div>
      </div>

    </div>

    <!-- Delete confirmations go through the global <ConfirmDialog /> (App.vue) via useConfirm() -->

    <!-- AddToolModal -->
    <AddToolModal
      v-if="showAddModal"
      :bot-id="botId"
      @close="showAddModal = false"
      @open-http-modal="onOpenHttpModal"
      @create-tool-flow="onCreateToolFlow"
    />

    <!-- HttpMcpConfigModal -->
    <HttpMcpConfigModal
      v-if="showHttpModal"
      :bot-id="botId"
      @close="showHttpModal = false"
      @server-added="onServerAdded"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useConfirm } from 'primevue/useconfirm';
import api from '../../services/api';
import { showToast } from '../../services/toastService';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';
import AddToolModal from '../../components/mcp/AddToolModal.vue';
import HttpMcpConfigModal from '../../components/mcp/HttpMcpConfigModal.vue';
import toolFlowService from '../../services/toolFlowService';
import TagMultiSelect from '../../components/flow/TagMultiSelect.vue';
import InfoTip from '../../components/ui/InfoTip.vue';

const { t } = useI18n();
const confirm = useConfirm();
const route = useRoute();
const router = useRouter();
const botId = computed(() => route.params.id);
const workspaceId = computed(() => route.params.workspaceId);

const loading = ref(true);
const error = ref(null);
const configuredMcps = ref([]);
const toolFlows = ref([]);

const toolFlowToDelete = ref(null);
const mcpToDelete = ref(null);
const showAddModal = ref(false);
const showHttpModal = ref(false);
// Per-row remount counter for the visibility switches (see the template comment).
const switchNonce = ref({});

function resetSwitch(id) {
  switchNonce.value[id] = (switchNonce.value[id] || 0) + 1;
}

async function loadData() {
  loading.value = true;
  error.value = null;
  try {
    const [mcpRes, tfRes] = await Promise.all([
      api.get(`/mcp/configured?botId=${botId.value}`),
      toolFlowService.list(workspaceId.value, botId.value),
    ]);
    configuredMcps.value = mcpRes.data.configured || [];
    toolFlows.value = tfRes.data.toolFlows || [];
  } catch (err) {
    error.value = err.response?.data?.error || t('botEditor.tools.loadDataError');
  } finally {
    loading.value = false;
  }
}

async function onCreateToolFlow() {
  showAddModal.value = false;
  try {
    const res = await toolFlowService.create(workspaceId.value, botId.value, t('botEditor.tools.newToolDefaultName'));
    router.push({ name: 'ToolFlowEditor', params: { id: botId.value, toolFlowId: res.data.toolFlow._id } });
  } catch (err) {
    showToast(t('botEditor.tools.errorCreateToolFlow'));
  }
}

function editToolFlow(tf) {
  router.push({ name: 'ToolFlowEditor', params: { id: botId.value, toolFlowId: tf._id } });
}

function openDeleteToolFlowModal(tfId) {
  toolFlowToDelete.value = tfId;
  const clearToolFlowToDelete = () => { toolFlowToDelete.value = null; };
  confirm.require({
    header: t('botEditor.tools.deleteToolFlowModalTitle'),
    message: t('botEditor.tools.deleteToolFlowModalMessage'),
    icon: 'warning',
    acceptLabel: t('botEditor.tools.deleteModalConfirm'),
    rejectLabel: t('botEditor.tools.deleteModalCancel'),
    acceptProps: { id: 'btn-confirm-delete-tool-flow', severity: 'danger' },
    rejectProps: { id: 'btn-cancel-delete-tool-flow', severity: 'secondary' },
    defaultFocus: 'reject',
    accept: confirmDeleteToolFlow,
    // reject = Cancel button; onHide = X / Esc (ConfirmDialog does not call reject for those)
    reject: clearToolFlowToDelete,
    onHide: clearToolFlowToDelete,
  });
}

async function confirmDeleteToolFlow() {
  // ConfirmDialog closes before the request runs: capture the id and clear the ref now,
  // so a confirmation opened while this request is in flight is not clobbered.
  const tfId = toolFlowToDelete.value;
  toolFlowToDelete.value = null;
  if (!tfId) return;
  try {
    await toolFlowService.remove(workspaceId.value, tfId);
    toolFlows.value = toolFlows.value.filter(tf => tf._id !== tfId);
  } catch (err) {
    showToast(t('botEditor.tools.errorDeleteToolFlow'));
  }
}

async function toggleToolFlow(tfId, enabled) {
  try {
    await toolFlowService.toggle(workspaceId.value, tfId, enabled);
    const tf = toolFlows.value.find(t => t._id === tfId);
    if (tf) tf.enabled = enabled;
  } catch (err) {
    resetSwitch(tfId);
    showToast(t('botEditor.tools.errorToggleToolFlow'));
  }
}

function bmsDisplayName(bms) {
  return bms.name || t('botEditor.tools.customMcpDefaultName');
}

function bmsDescription(bms) {
  return bms.endpointUrl || '';
}

function bmsIcon() {
  return 'power';
}

function bmsIconColor(bms) {
  if (bms.enabled) return 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)';
  return 'linear-gradient(135deg, #4299e1 0%, #2b6cb0 100%)';
}

async function toggleMcp(mcpId, enabled) {
  try {
    await api.put(`/mcp/${mcpId}`, { botId: botId.value, enabled });
    const mcp = configuredMcps.value.find(m => m._id === mcpId);
    if (mcp) mcp.enabled = enabled;
  } catch (err) {
    resetSwitch(mcpId);
    showToast(t('botEditor.tools.toggleError'));
  }
}

async function updateMcpTags(bms, newTags) {
  try {
    await api.put(`/mcp/${bms._id}`, { botId: botId.value, tags: newTags });
    bms.tags = newTags;
  } catch (err) {
    showToast(t('botEditor.tools.tagsError'));
  }
}

async function updateToolFlowTags(tf, newTags) {
  try {
    await toolFlowService.update(workspaceId.value, tf._id, { tags: newTags });
    tf.tags = newTags;
  } catch (err) {
    showToast(t('botEditor.tools.tagsError'));
  }
}

function openDeleteModal(mcpId) {
  mcpToDelete.value = mcpId;
  const clearMcpToDelete = () => { mcpToDelete.value = null; };
  confirm.require({
    header: t('botEditor.tools.deleteModalTitle'),
    message: t('botEditor.tools.deleteModalMessage'),
    icon: 'warning',
    acceptLabel: t('botEditor.tools.deleteModalConfirm'),
    rejectLabel: t('botEditor.tools.deleteModalCancel'),
    acceptProps: { id: 'btn-confirm-delete-mcp', severity: 'danger' },
    rejectProps: { id: 'btn-cancel-delete-mcp', severity: 'secondary' },
    defaultFocus: 'reject',
    accept: confirmDeleteMcp,
    // reject = Cancel button; onHide = X / Esc (ConfirmDialog does not call reject for those)
    reject: clearMcpToDelete,
    onHide: clearMcpToDelete,
  });
}

async function confirmDeleteMcp() {
  // Same as confirmDeleteToolFlow: capture the id and clear the ref before the request.
  const mcpId = mcpToDelete.value;
  mcpToDelete.value = null;
  if (!mcpId) return;
  try {
    await api.delete(`/mcp/${mcpId}?botId=${botId.value}`);
    await loadData();
  } catch (err) {
    showToast(t('botEditor.tools.errorDelete'));
  }
}

function onOpenHttpModal() {
  showAddModal.value = false;
  showHttpModal.value = true;
}

async function onServerAdded() {
  showHttpModal.value = false;
  await loadData();
}

onMounted(() => loadData());
</script>

<style scoped>
.tools-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
}

.tool-card {
  background: #374151;
  border: 2px solid #4b5563;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
}

.tool-card:hover {
  border-color: #6b7280;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
}

.tool-card.is-enabled {
  border-color: #48bb78;
  background: #1a3a2a;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.tool-icon-container {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.tool-icon-container .material-icons {
  font-size: 28px;
  color: white;
}

.tool-info {
  flex: 1;
}

.tool-info h3 {
  font-size: 16px;
  font-weight: 600;
  color: #f7fafc;
  margin-bottom: 4px;
}

.card-body {
  padding-top: 16px;
  border-top: 1px solid #4b5563;
}

.tool-description {
  color: #d1d5db;
  font-size: 14px;
  margin-bottom: 12px;
  line-height: 1.5;
}

.tool-meta {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.add-card {
  border: 2px dashed #4b5563;
  background: transparent;
  cursor: pointer;
  min-height: 180px;
  transition: all 0.2s;
}

.add-card:hover {
  border-color: #6b7280;
  background: #1f2937;
}
</style>
